import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/lib/useProfile";
import { useNutritionTargets } from "@/lib/useNutritionTargets";
import { sumNutrients } from "@/lib/nutrientTotals";
import type { DailyLog, WeightLog } from "@/types";
import TrendChart from "@/components/TrendChart";
import { ProviderDeferNote } from "@/components/ProviderNote";

function lastNDates(n: number): string[] {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (n - 1 - i));
    return d.toISOString().slice(0, 10);
  });
}

export default function Trends() {
  const { profile, loading: profileLoading } = useProfile();
  const targets = useNutritionTargets(profile);
  const [dailyLogs, setDailyLogs] = useState<DailyLog[]>([]);
  const [weightLogs, setWeightLogs] = useState<WeightLog[]>([]);

  const dates = lastNDates(7);

  useEffect(() => {
    const start = dates[0];
    const end = dates[dates.length - 1];
    api.logs.daily.range(start, end).then(setDailyLogs);
    api.logs.weight.range(start, end).then(setWeightLogs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (profileLoading || !profile || !targets.calories) {
    return <div className="p-6 text-sm text-forest-500">Loading your trends…</div>;
  }

  const caloriePoints = dates.map((date) => {
    const log = dailyLogs.find((l) => l.logDate === date);
    return { label: date.slice(5), value: log ? sumNutrients(log.foodEntries).calories ?? 0 : null };
  });

  const weightPoints = dates.map((date) => {
    const w = weightLogs.find((l) => l.logDate === date);
    return { label: date.slice(5), value: w ? Number(w.weightKg) : null };
  });

  // Rough current expected weight-gain band, anchored to pre-pregnancy weight.
  const gainLow = profile.prePregnancyWeightKg + targets.weightGainBand!.totalRangeKg[0] * 0.3;
  const gainHigh = profile.prePregnancyWeightKg + targets.weightGainBand!.totalRangeKg[1] * 0.3;

  return (
    <div className="mx-auto max-w-md px-4 pb-8 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
      <h1 className="text-2xl font-semibold text-forest-700">Trends</h1>
      <p className="mt-1 text-sm text-forest-500">Last 7 days — a single off day won't throw this off.</p>

      <section className="mt-6 rounded-card border border-line bg-white p-4">
        <h2 className="text-sm font-medium text-ink">Calories vs. target</h2>
        <TrendChart points={caloriePoints} target={targets.calories} unit="kcal" />
      </section>

      <section className="mt-4 rounded-card border border-line bg-white p-4">
        <h2 className="text-sm font-medium text-ink">Weight vs. expected range</h2>
        <TrendChart points={weightPoints} band={{ low: gainLow, high: gainHigh }} unit="kg" />
        <p className="mt-2 text-xs text-forest-500">
          Shaded band is a rough guide based on your pre-pregnancy BMI category — not a target to
          hit exactly.
        </p>
      </section>

      <ProviderDeferNote className="mt-6 text-center" />
    </div>
  );
}
